// MAIN-844: draft/published state and manual item ordering for the existing
// Menu editor. Pure helpers only, so the ordering and badge rules can be tested
// without mounting the table.

export const MENU_ITEM_STATUS = {
  DRAFT: "Draft",
  PUBLISHED: "Published",
};

/**
 * Items saved before MAIN-844 have no `status` at all. The API reports them as
 * Published; mirror that here so the table never renders a blank badge.
 */
export function resolveMenuItemStatus(item) {
  const status = String(item?.status ?? "").trim();
  if (status.toLowerCase() === MENU_ITEM_STATUS.DRAFT.toLowerCase()) {
    return MENU_ITEM_STATUS.DRAFT;
  }
  return MENU_ITEM_STATUS.PUBLISHED;
}

export function isDraft(item) {
  return resolveMenuItemStatus(item) === MENU_ITEM_STATUS.DRAFT;
}

/** Badge text + class for the Status column. */
export function getMenuStatusBadge(item) {
  return isDraft(item)
    ? { label: "Draft", className: "menu-status-badge menu-status-badge--draft" }
    : {
        label: "Published",
        className: "menu-status-badge menu-status-badge--published",
      };
}

/** The action a row's overflow menu should offer, given its current state. */
export function getStatusToggleAction(item) {
  return isDraft(item)
    ? { label: "Publish", nextStatus: MENU_ITEM_STATUS.PUBLISHED }
    : { label: "Unpublish (save as draft)", nextStatus: MENU_ITEM_STATUS.DRAFT };
}

/**
 * Order items for display: by `order`, then name, with anything lacking an
 * order after the ordered ones. Mirrors the API's sortMenuItems so the table
 * does not flicker into a different arrangement before a refetch lands.
 */
export function sortMenuItems(items = []) {
  return [...items]
    .map((item, index) => {
      return { item, index };
    })
    .sort((left, right) => {
      const leftOrder = Number.isFinite(Number(left.item?.order))
        ? Number(left.item.order)
        : Number.POSITIVE_INFINITY;
      const rightOrder = Number.isFinite(Number(right.item?.order))
        ? Number(right.item.order)
        : Number.POSITIVE_INFINITY;
      if (leftOrder !== rightOrder) return leftOrder - rightOrder;
      const leftName = String(left.item?.itemName ?? "").toLowerCase();
      const rightName = String(right.item?.itemName ?? "").toLowerCase();
      if (leftName !== rightName) return leftName < rightName ? -1 : 1;
      return left.index - right.index;
    })
    .map((entry) => entry.item);
}

/**
 * Move one item up or down by a single position.
 *
 * Returns the same array reference when the move is impossible (already at an
 * end, unknown id), so callers can skip a pointless mutation round-trip.
 */
export function moveMenuItem(items = [], itemId, direction) {
  const step = direction === "up" ? -1 : 1;
  const index = items.findIndex((item) => String(item?._id) === String(itemId));
  if (index === -1) return items;
  const target = index + step;
  if (target < 0 || target >= items.length) return items;

  const next = [...items];
  const [moved] = next.splice(index, 1);
  next.splice(target, 0, moved);
  return next;
}

export function canMoveUp(items = [], itemId) {
  return items.findIndex((item) => String(item?._id) === String(itemId)) > 0;
}

export function canMoveDown(items = [], itemId) {
  const index = items.findIndex((item) => String(item?._id) === String(itemId));
  return index !== -1 && index < items.length - 1;
}

/** The `orderedIds` payload for the reorderMenuItems mutation. */
export function toOrderedIds(items = []) {
  return items.map((item) => item?._id).filter(Boolean);
}

/** True when the arrangement actually changed and is worth persisting. */
export function hasOrderChanged(before = [], after = []) {
  const left = toOrderedIds(before);
  const right = toOrderedIds(after);
  if (left.length !== right.length) return true;
  return left.some((id, index) => String(id) !== String(right[index]));
}

/**
 * Reordering is only meaningful within one category, because `order` is stored
 * per caterer+category. A mixed list (the "All categories" view) must not offer
 * the arrows, or item 3 of Drinks would be renumbered against Mains.
 */
export function canReorder({ category, items = [] } = {}) {
  if (!category) return false;
  return items.length > 1;
}

/**
 * Total page count for the list Pagination control. `count` is `undefined`
 * before the list query resolves (and `rowsPerPage` could theoretically be
 * 0) — MUI's Pagination throws a prop-type warning if it ever receives
 * anything other than a finite integer, so this always returns at least 1.
 */
export function getMenuPaginationCount({ count, rowsPerPage } = {}) {
  const total = Number(count);
  const perPage = Number(rowsPerPage);
  if (!Number.isFinite(total) || !Number.isFinite(perPage) || perPage <= 0) {
    return 1;
  }
  return Math.max(1, Math.ceil(total / perPage));
}

/**
 * Optional numeric fields that a Draft is allowed to leave blank.
 *
 * `price` and `additionalPkdPrice` are GraphQL `Float` and `minQuantity` is
 * cast to a Number by Mongoose, so an empty string is never a legal value for
 * any of them: `Float` rejects `""` outright ("Float cannot represent non
 * numeric value") and `minQuantity: ""` persists as `null`, which then renders
 * as "null min". Blank means "not filled in yet", so the field is omitted from
 * the payload entirely rather than sent as "".
 */
export const OPTIONAL_MENU_NUMBER_FIELDS = [
  "price",
  "minQuantity",
  "additionalPkdPrice",
];

export function isBlankValue(value) {
  return value === "" || value === null || value === undefined;
}

/** Drop blank optional numeric fields from a menu form payload. */
export function stripBlankMenuNumbers(params = {}) {
  const cleaned = { ...params };
  OPTIONAL_MENU_NUMBER_FIELDS.forEach((field) => {
    if (isBlankValue(cleaned[field])) delete cleaned[field];
  });
  return cleaned;
}

/**
 * The Price column for one row.
 *
 * A Draft may have neither a price nor a minimum quantity, and the old
 * `formatter.format(price) + " / " + minQuantity + " min"` rendered "$NaN"
 * and "null min" for those. Show only the parts that actually have a value.
 */
export function formatMenuPriceAndMin(item, formatter) {
  const price = Number(item?.price);
  const minQuantity = Number(item?.minQuantity);
  const hasPrice = !isBlankValue(item?.price) && Number.isFinite(price);
  const hasMin =
    !isBlankValue(item?.minQuantity) &&
    Number.isFinite(minQuantity) &&
    minQuantity > 0;

  if (!hasPrice && !hasMin) return "—";
  const pricePart = hasPrice ? formatter.format(price) : "—";
  return hasMin ? `${pricePart} / ${minQuantity} min` : pricePart;
}

/** Copy for the empty state, which differs by why the list is empty. */
export function getMenuEmptyState({ search, category, hasAnyItems } = {}) {
  if (search) {
    return {
      variant: "no-search-results",
      title: "No items match your search",
      body: "Try a different name, or clear the search to see the full menu.",
      showCreate: false,
    };
  }
  if (category && hasAnyItems) {
    return {
      variant: "empty-category",
      title: "No items in this category yet",
      body: "Add your first item to this category, or switch to another category.",
      showCreate: true,
    };
  }
  return {
    variant: "no-menu",
    title: "Start building your menu",
    body: "Add items one at a time. Save them as drafts while you work, then publish when the menu is ready for customers.",
    showCreate: true,
  };
}
